import { useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { AuthContext, Profile, SignUpData } from "./auth-context";
import { lovable } from "@/integrations/lovable";
import { getAuthHintCookie, setAuthHintCookie, clearAuthHintCookie } from "@/lib/auth-hint-cookie";

export { useAuth } from "./auth-context";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authHint, setAuthHint] = useState<ReturnType<typeof getAuthHintCookie>>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState<string>("activo");
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [mustResetPassword, setMustResetPassword] = useState(false);

  const checkAdminRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      return !error && !!data;
    } catch {
      return false;
    }
  };

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }
      return data as Profile | null;
    } catch (err) {
      console.error("Error in fetchProfile:", err);
      return null;
    }
  };

  const handleAccountStatus = async (profileData: Profile | null) => {
    if (!profileData) return;
    const status = (profileData as any).account_status || "activo";
    setAccountStatus(status);

    // Check must_reset_password flag
    setMustResetPassword(!!(profileData as any).must_reset_password);

    if (status === "suspendido") {
      return true;
    }

    if (status === "desactivado") {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);
      setAccountStatus("activo");
      setMustResetPassword(false);
      toast({
        title: "Cuenta desactivada",
        description: "Tu cuenta ha sido desactivada por el administrador. Contacta soporte para más información.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const clearMustResetPassword = () => setMustResetPassword(false);

  useEffect(() => {
    setAuthHint(getAuthHintCookie());
  }, []);

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
      if (profileData) {
        setAccountStatus((profileData as any).account_status || "activo");
        setMustResetPassword(!!(profileData as any).must_reset_password);
        setAuthHintCookie({ userType: profileData.user_type, isAdmin });
      }
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") return;

        setSession(session);
        setUser(session?.user ?? null);
        setEmailConfirmed(!!session?.user?.email_confirmed_at);

        if (session?.user) {
          setTimeout(() => {
            Promise.all([
              fetchProfile(session.user.id),
              checkAdminRole(session.user.id),
            ]).then(async ([profileData, adminStatus]) => {
              const allowed = await handleAccountStatus(profileData);
              if (allowed === false) return;
              setProfile(profileData);
              setIsAdmin(adminStatus);
              setAuthHintCookie({ userType: profileData?.user_type ?? null, isAdmin: adminStatus });
            });
          }, 0);
        } else {
          setProfile(null);
          setIsAdmin(false);
          clearAuthHintCookie();
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setEmailConfirmed(!!session?.user?.email_confirmed_at);

      if (session?.user) {
        Promise.all([
          fetchProfile(session.user.id),
          checkAdminRole(session.user.id),
        ]).then(async ([profileData, adminStatus]) => {
          const allowed = await handleAccountStatus(profileData);
          if (allowed === false) {
            setLoading(false);
            return;
          }
          setProfile(profileData);
          setIsAdmin(adminStatus);
          setAuthHintCookie({ userType: profileData?.user_type ?? null, isAdmin: adminStatus });
          setLoading(false);
        });
      } else {
        clearAuthHintCookie();
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, userData: SignUpData) => {
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            user_type: userData.userType,
            full_name: userData.fullName,
            phone: userData.phone || "",
            address_street: userData.addressStreet || "",
            address_city: userData.addressCity || "",
            date_of_birth: userData.dateOfBirth || "",
            sex: userData.sex || "",
          },
        },
      });

      if (error) {
        return { error };
      }

      // Profile is created automatically by the on_auth_user_created trigger.
      if (data.user) {
        const profileData = await fetchProfile(data.user.id);
        setProfile(profileData);
      }

      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        return { error };
      }
      
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signInWithOAuth = async (provider: "google" | "apple") => {
    try {
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: `${window.location.origin}/auth/callback`,
      });
      if (result.error) return { error: result.error as Error };
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    clearAuthHintCookie();
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  const isSignedIn = !!user || (!loading && !!authHint);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isAdmin,
        isSignedIn,
        loading,
        accountStatus,
        emailConfirmed,
        mustResetPassword,
        signUp,
        signIn,
        signInWithOAuth,
        signOut,
        refreshProfile,
        clearMustResetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
